import { Component, computed, inject, input, output } from '@angular/core';
import jsPDF from 'jspdf';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { textoPrazo } from '../../models/canal-publico.model';
import { ManifestacaoStore } from '../manifestacao.store';

const DATA_LONGA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const HORA_CURTA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** Medidas do comprovante em milímetros. */
const MARGEM = 16;
const ALTURA_FAIXA = 26;
const ALTURA_LINHA = 5.5;
const LARGURA_ROTULO = 44;
const RODAPE = 280;

interface LinhaComprovante {
  rotulo: string;
  valor: string;
}

/** Tom do aviso que a API devolveu: informativo, de atenção ou de recusa. */
type TomAviso = 'info' | 'aviso' | 'erro';

@Component({
  selector: 'app-etapa-confirmacao-ouvidoria',
  templateUrl: './etapa-confirmacao-ouvidoria.component.html',
  styleUrls: ['./etapa-confirmacao-ouvidoria.component.css'],
  imports: [LucideModule],
})
export class EtapaConfirmacaoOuvidoriaComponent {
  private readonly alerta = inject(AlertaService);

  /**
   * Tudo o que esta etapa mostra vem do 201 gravado no store. Nada de valor fixo: o `@case (3)`
   * do pai só renderiza depois da resposta, então a abertura sempre existe aqui.
   */
  protected readonly store = inject(ManifestacaoStore);

  readonly servico = input('Manifestação');

  /** Pedido para o fluxo recomeçar; quem reinicia o wizard é o componente pai. */
  readonly novaManifestacao = output<void>();

  readonly abertura = computed(() => this.store.respostaAbertura());

  readonly protocolo = computed(() => this.abertura()?.protocolo ?? '');

  readonly recusada = computed(() => this.abertura()?.situacao === 'Recusada');

  readonly anonima = computed(() => this.store.anonimo());

  /** Vem do que o cidadão enviou, e não do token: no gov.br pode não haver sessão. */
  readonly saudacao = computed(() => {
    const nome = this.anonima() ? '' : this.store.dados().nomeCompleto.trim();

    return nome ? `Sr(a). ${nome},` : 'Prezado(a) cidadão(ã),';
  });

  readonly solicitante = computed(() =>
    this.anonima() ? 'Anônimo' : this.store.dados().nomeCompleto.trim() || 'Não identificado',
  );

  readonly dataAbertura = computed(() => {
    const data = this.paraData(this.abertura()?.dataAbertura);

    return data ? `${DATA_LONGA.format(data)} às ${HORA_CURTA.format(data)}` : '';
  });

  readonly dataPrazo = computed(() => {
    const data = this.paraData(this.abertura()?.dataLimiteResposta);

    return data ? DATA_LONGA.format(data) : '';
  });

  /** "30 dias úteis" — o prazo CONGELADO no ato da abertura, não o do canal de agora. */
  readonly prazoTexto = computed(() => {
    const resposta = this.abertura();

    return resposta ? textoPrazo(resposta.prazoResposta, resposta.unidadePrazo) : '';
  });

  /**
   * O aviso da API — retenção por trava ou motivo da recusa automática. A recusa é erro, a
   * retenção é atenção; sem aviso, não há bloco.
   */
  readonly aviso = computed(() => this.abertura()?.aviso?.trim() ?? '');

  readonly tomAviso = computed<TomAviso>(() => {
    if (this.recusada()) return 'erro';

    return this.abertura()?.retida ? 'aviso' : 'info';
  });

  /** Só existe quando a ouvidoria de destino natural estava travada. */
  readonly redirecionamento = computed(() => {
    const resposta = this.abertura();

    if (!resposta?.redirecionada || !resposta.ouvidoriaOrigem) return '';

    return (
      `Sua manifestação foi encaminhada da ${resposta.ouvidoriaOrigem} para a ` +
      `${resposta.ouvidoria ?? 'ouvidoria responsável'}, que está com o atendimento no momento.`
    );
  });

  /** A frase do e-mail só é verdade quando o back-end de fato enfileirou a notificação. */
  readonly notificaPorEmail = computed(() => this.abertura()?.notificacaoEnfileirada === true);

  /** Monta o comprovante da manifestação e entrega o arquivo ao navegador. */
  salvarPdf(): void {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      this.desenharComprovante(doc);
      doc.save(`comprovante-ouvidoria-${this.protocolo()}.pdf`);
    } catch (erro) {
      console.error('Erro ao gerar o comprovante:', erro);

      this.alerta.erro({
        titulo: 'Não foi possível gerar o comprovante',
        texto: 'Tente novamente em instantes.',
      });
    }
  }

  private paraData(valor: string | undefined | null): Date | null {
    if (!valor) return null;

    const data = new Date(valor);

    return Number.isNaN(data.getTime()) ? null : data;
  }

  private desenharComprovante(doc: jsPDF): void {
    const larguraPagina = doc.internal.pageSize.getWidth();
    const larguraUtil = larguraPagina - MARGEM * 2;
    const resposta = this.abertura();

    this.desenharFaixa(doc, larguraPagina);

    let y = ALTURA_FAIXA + 14;

    y = this.desenharTitulo(doc, 'Número do protocolo', y);
    y = this.desenharProtocolo(doc, y);

    y = this.desenharSecao(doc, 'Dados da manifestação', y, [
      { rotulo: 'Tipo', valor: `Ouvidoria — ${resposta?.tipo || this.servico()}` },
      { rotulo: 'Assunto', valor: resposta?.assunto ?? '' },
      { rotulo: 'Situação', valor: resposta?.situacao ?? '' },
      { rotulo: 'Data de abertura', valor: this.dataAbertura() },
      { rotulo: 'Prazo de resposta', valor: `${this.prazoTexto()} (até ${this.dataPrazo()})` },
      { rotulo: 'Ouvidoria', valor: resposta?.ouvidoria ?? 'Ouvidoria do órgão' },
      { rotulo: 'Solicitante', valor: this.solicitante() },
    ]);

    if (this.aviso()) {
      y = this.desenharTexto(doc, 'Aviso', this.aviso(), y, larguraUtil);
    }

    this.desenharTexto(
      doc,
      'Como acompanhar',
      'Com este protocolo você acompanha o andamento da manifestação pela aba "Acompanhe sua ' +
        'solicitação" no Portal de Atendimento, pela central telefônica ou fazendo login para ' +
        'complementar e reiterar esta e outras demandas abertas anteriormente.',
      y,
      larguraUtil,
    );

    this.desenharRodape(doc, larguraUtil);
  }

  private desenharFaixa(doc: jsPDF, larguraPagina: number): void {
    doc.setFillColor(11, 36, 70);
    doc.rect(0, 0, larguraPagina, ALTURA_FAIXA, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Comprovante de manifestação', MARGEM, 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(this.abertura()?.ouvidoria ?? 'Ouvidoria do órgão', MARGEM, 19);
  }

  private desenharTitulo(doc: jsPDF, texto: string, y: number): number {
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(texto.toUpperCase(), MARGEM, y);

    return y + 8;
  }

  private desenharProtocolo(doc: jsPDF, y: number): number {
    doc.setTextColor(43, 93, 150);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(this.protocolo(), MARGEM, y);

    return y + 12;
  }

  private desenharSecao(
    doc: jsPDF,
    titulo: string,
    inicio: number,
    linhas: readonly LinhaComprovante[],
  ): number {
    let y = this.desenharTitulo(doc, titulo, inicio);

    doc.setFontSize(10);

    for (const linha of linhas) {
      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'bold');
      doc.text(`${linha.rotulo}:`, MARGEM, y);

      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'normal');
      doc.text(linha.valor, MARGEM + LARGURA_ROTULO, y);

      y += ALTURA_LINHA;
    }

    return y + 6;
  }

  /** Bloco de texto corrido, quebrado na largura da página. */
  private desenharTexto(
    doc: jsPDF,
    titulo: string,
    texto: string,
    inicio: number,
    larguraUtil: number,
  ): number {
    let y = this.desenharTitulo(doc, titulo, inicio);

    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    for (const linha of doc.splitTextToSize(texto, larguraUtil) as string[]) {
      doc.text(linha, MARGEM, y);
      y += ALTURA_LINHA;
    }

    return y + 6;
  }

  private desenharRodape(doc: jsPDF, larguraUtil: number): void {
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    const aviso =
      'Guarde este comprovante: o número do protocolo é o que identifica a sua manifestação em ' +
      'qualquer canal de atendimento da Prefeitura.';

    let y = RODAPE;

    for (const linha of doc.splitTextToSize(aviso, larguraUtil) as string[]) {
      doc.text(linha, MARGEM, y);
      y += 4;
    }

    doc.text(`Emitido em ${this.dataAbertura()}.`, MARGEM, y);
  }
}
